<template>
  <div class="am-payment-method-popup">
    <div class="am-payment-method-title-bar am-main-top-box">
      <span class="am-payment-method-title am-main-top-box-title"><strong>{{ title }}</strong></span>
    </div>
    <form class="am-payment-method-form">
      <!-- Base Office Price & Price After Discount -->
      <div class="form-row">
        <div class="form-group col-md-6">
          <label class="w-100">Base Price Per Office</label>
          <input type="text" class="form-control" placeholder="0" v-model="paymentMethod.office_amount" @keypress="isNumber($event, paymentMethod.office_amount)" @change="formatNumber(paymentMethod, 'office_amount')" :disabled="(!isEdit && ioPackageName === 'MGA') || ioPackageName !== 'MGA'">
        </div>
        <div class="form-group col-md-6">
          <label class="w-100">Client Price Per Office</label>
          <input type="text" class="form-control" disabled v-model="officeAmountAfterDiscount">
        </div>
      </div>

      <!-- Payment Type & Lifetime Discount -->
      <div class="form-row">
        <div class="form-group col-md-6">
          <label class="w-100">Payment Type</label>
          <select class="form-control" v-model="paymentMethod.payment_type" @change="clearPaymentSection" :disabled="!isEdit">
            <option v-for="paymentType in paymentTypes" :key="paymentType.value" :value="paymentType.value">{{paymentType.display}}</option>
          </select>
        </div>
        <div class="form-group col-md-6">
          <label class="w-100">Lifetime Discount</label>
          <div class="form-row">
            <div class="col-md-3">
              <select class="form-control" v-model="paymentMethod.lifetime_discount.amountType" :disabled="!isEdit">
                <option v-for="discountType in discountTypes" :key="discountType.value" :value="discountType.value">{{discountType.display}}</option>
              </select>
            </div>
            <div class="col-md-9">
              <input type="text" class="form-control" placeholder="0" v-model="paymentMethod.lifetime_discount.amount" @keypress="isNumber($event, paymentMethod.lifetime_discount.amount)" @change="formatNumber(paymentMethod.lifetime_discount, 'amount')" :disabled="!isEdit">
            </div>
          </div>
        </div>
      </div>

      <!-- Check -->
      <div class="form-row" v-if="paymentMethod.payment_type === 'CHECK'">
        <div class="form-group col-md-6">
          <label class="w-100">Description</label>
          <input type="text" class="form-control" v-model="paymentMethod.checkDescription">
        </div>
      </div>

      <!-- Bank Account -->
      <div class="form-row" v-if="paymentMethod.payment_type === 'ACH'">
        <div class="form-group col-md-6">
          <label class="w-100">Account #</label>
          <input type="text" class="form-control" v-model="paymentMethod.account" @keypress="isWholeNumber($event)" maxlength="64" :disabled="!isEdit">
        </div>
        <div class="form-group col-md-6">
          <label class="w-100">Routing #</label>
          <input type="text" class="form-control" :class="{'is-invalid': !bank.valid}" v-model="paymentMethod.routing" @keypress="isWholeNumber($event)" maxlength="9" :disabled="!isEdit">
          <div :class="{'text-danger': !bank.valid}">{{bank.name}}</div>
        </div>
      </div>

      <!-- Credit Card -->
      <div v-if="paymentMethod.payment_type === 'CC'">
        <div class="form-row">
          <div class="form-group col-md-6">
            <label class="w-100">Credit Card Type</label>
            <select class="form-control" v-model="paymentMethod.routing" :disabled="!isEdit">
              <option v-for="creditCardType in creditCardTypes" :key="creditCardType.value" :value="creditCardType.value">{{creditCardType.display}}</option>
            </select>
          </div>
          <div class="form-group col-md-6">
            <label class="w-100">Card Number</label>
            <input type="text" class="form-control" v-model="paymentMethod.account" @keypress="isWholeNumber($event)" :disabled="!isEdit">
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-md-6">
            <label class="w-100">Card Expiration</label>
            <div class="form-row">
              <div class="form-group col-md-6">
                <select class="form-control" v-model="ccExpirationDate.month" :disabled="!isEdit">
                  <option v-for="month in months" :key="month" :value="month">{{month}}</option>
                </select>
              </div>
              <div class="form-group col-md-6">
                <select class="form-control" v-model="ccExpirationDate.year" :disabled="!isEdit">
                  <option v-for="year in years" :key="year.value" :value="year.value">{{year.display}}</option>
                </select>
              </div>
            </div>
          </div>

          <div class="form-group col-md-6">
            <label class="w-100">Security Code</label>
            <input type="text" class="form-control" v-model="paymentMethod.cc_detail.code" @keypress="isWholeNumber($event)" maxlength="4" :disabled="!isEdit">
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-md-6">
            <label class="w-100">Billing Name</label>
            <input type="text" class="form-control" v-model="paymentMethod.cc_detail.bill_name" :disabled="!isEdit">
          </div>
          <div class="form-group col-md-6">
            <label class="w-100">Billing Address</label>
            <input type="text" class="form-control" v-model="paymentMethod.cc_detail.bill_address" :disabled="!isEdit">
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-md-6">
            <label class="w-100">City</label>
            <input type="text" class="form-control" v-model="paymentMethod.cc_detail.bill_city" :disabled="!isEdit" @keypress="isCityField">
          </div>
          <div class="form-group col-md-2">
            <label class="w-100">State</label>
            <input type="text" class="form-control" maxlength="2" v-model="paymentMethod.cc_detail.bill_state" :disabled="!isEdit" @keypress="isLetter">
          </div>
          <div class="form-group col-md-4">
            <label class="w-100">Zip</label>
            <input type="text" class="form-control" maxlength="10" v-model="paymentMethod.cc_detail.bill_zip" :disabled="!isEdit" @keypress="isNumberWithHyphen($event, paymentMethod.cc_detail.bill_zip)">
          </div>
        </div>
      </div>

      <!-- Discounts -->
      <discounts :discounts="paymentMethod.discounts" :isEdit="isEdit" :deleteDiscount="true" @updateDiscounts="updateDiscounts"></discounts>

      <!-- Blue Line -->
      <div class="form-row">
        <div class="form-group col-md-12">
          <div class="am-blue-border"></div>
        </div>
      </div>

      <!-- Cancel & Submit -->
      <div class="form-row">
        <div class="form-group col-md-12">
          <div class="am-discount-align-bottom-buttons">
            <div class="float-right">
              <button type="button" class="btn btn-secondary" @click="closeModal">Cancel</button>
              <button type="button" class="btn btn-primary" @click="submit"  v-if="isEdit" :disabled="paymentMethod.payment_type === 'ACH' && !bank.valid">Submit</button>
            </div>
          </div>
        </div>
      </div>
    </form>
    <modal name="MessagePopup" classes="am-vue-modal" :clickToClose="false" :pivotY="0.4" :adaptive="true" width="450px" height="auto">
      <message-popup :confirmDelete="false" :errors="messageString" messageHeader="Error" :isError="true"></message-popup>
    </modal>
  </div>
</template>

<script>
import api from './../http'
import moment from 'moment'
import Discounts from '@/components/DiscountSection'
import MessagePopup from './MessagePopup'
import Datepicker from 'vuejs-datepicker'

export default {
  name: 'PaymentMethod',
  data () {
    return {
      paymentTypes: [
        {
          value: '',
          display: '-Select Payment Type-'
        },
        {
          value: 'ACH',
          display: 'Bank Account'
        },
        {
          value: 'CC',
          display: 'Credit Card'
        },
        {
          value: 'CHECK',
          display: 'Check'
        }
      ],
      discountTypes: [
        {
          value: 'A',
          display: '$'
        },
        {
          value: 'P',
          display: '%'
        }
      ],
      creditCardTypes: [
        {
          value: '000000001',
          display: 'VISA'
        },
        {
          value: '000000002',
          display: 'Master Card'
        },
        {
          value: '000000003',
          display: 'American Express'
        },
        {
          value: '000000004',
          display: 'Discover'
        }
      ],
      paymentMethod: {
        id: 0,
        acct_id: 0,
        routing: '',
        account: '',
        payment_type: '',
        office_amount: 0,
        old_id: null,
        lifetime_discount: {
          id: 0,
          amountType: 'A',
          amount: '0',
          employeeId: this.$store.state.auth.user.id
        },
        discounts: [{
          id: 0,
          amountType: 'A',
          amount: '0',
          effectiveDate: '',
          expirationDate: '',
          employeeId: this.$store.state.auth.user.id
        }],
        cc_detail: {
          id: 0,
          expdate: '',
          code: '',
          bill_name: '',
          bill_address: '',
          bill_city: '',
          bill_state: '',
          bill_zip: ''
        },
        checkDescription: ''
      },
      ioPackageName: -1,
      bank: {
        valid: false,
        name: ''
      },
      ccExpirationDate: {
        month: 1,
        year: this.currentYearLastTwoDigits
      },
      messageString: ''
    }
  },

  props: {
    id: {
      type: Number
    },
    masterId: {
      type: Number
    },
    isEdit: {
      type: Boolean,
      default: true
    }
  },

  mounted () {
    let self = this
    api.makeRequest('backoffice/lite-switch/' + this.masterId).then(
      (data) => {
        self.ioPackageName = data.data.results.ioPackageName
      }
    )
    if (this.id !== 0) {
      api.makeRequest('backoffice/payment-method/' + this.id).then(
        (data) => {
          self.paymentMethod = data.data.results
          self.paymentMethod.office_amount = self.paymentMethod.office_amount.toFixed(2)

          if (self.paymentMethod.lifetime_discount === null) {
            self.setBlankLifetimeDiscount()
          }
          if (self.paymentMethod.discounts.length === 0) {
            self.setBlankDiscount()
          }
          if (self.paymentMethod.cc_detail === null) {
            self.setBlankCcDetail()
          } else {
            let expdate = self.paymentMethod.cc_detail.expdate.split('/')
            self.ccExpirationDate.month = parseInt(expdate[0])
            self.ccExpirationDate.year = parseInt(expdate[1].slice(-2))
          }
        }
      )
    } else {
      api.makeRequest('backoffice/base-office-price/' + this.masterId).then(
        (data) => {
          self.paymentMethod.office_amount = data.data.results.priceMonthly
        }
      )
    }
    this.paymentMethod.acct_id = this.masterId
  },

  components: {
    Discounts,
    MessagePopup,
    Datepicker
  },

  computed: {
    officeAmountAfterDiscount () {
      let self = this
      let afterDiscount = self.paymentMethod.office_amount

      if (self.paymentMethod.lifetime_discounts !== null && !isNaN(self.paymentMethod.lifetime_discount.amount)) {
        afterDiscount -= (self.paymentMethod.lifetime_discount.amountType === 'P') ? self.paymentMethod.lifetime_discount.amount / 100 * self.paymentMethod.office_amount : self.paymentMethod.lifetime_discount.amount
      }

      let sortedDiscounts = JSON.parse(JSON.stringify(self.paymentMethod.discounts))
      sortedDiscounts.sort(function (a, b) {
        if (a.amountType === b.amountType) {
          return 0
        }
        return (a.amountType < b.amountType) ? -1 : 1
      })

      sortedDiscounts.forEach(function (discount) {
        let effDate = moment(discount.effectiveDate, 'YYYY-MM-DD')
        let expDate = moment(discount.expirationDate, 'YYYY-MM-DD')
        let today = moment(new Date().toISOString().slice(0, 10), 'YYYY-MM-DD')

        if (today.diff(effDate) >= 0 && (!expDate.isValid() || today.diff(expDate) <= 0) && !isNaN(discount.amount)) {
          afterDiscount -= (discount.amountType === 'P') ? discount.amount / 100 * afterDiscount : discount.amount
        }
      })

      return afterDiscount.toFixed(2)
    },

    baseOfficePriceDisplay () {
      return this.paymentMethod.office_amount.toFixed(2)
    },

    months () {
      return this.range(1, 12)
    },

    years () {
      let years = []
      this.range(this.currentYear, this.currentYear + 15).forEach(function (year) {
        years.push({
          value: ('' + year).slice(-2),
          display: year
        })
      })
      return years
    },

    currentYear () {
      let today = new Date()
      return parseInt(today.getFullYear())
    },

    currentYearLastTwoDigits () {
      return ('' + this.currentYear).slice(-2)
    },

    title () {
      if (this.id === 0) {
        return 'Add New Payment Method'
      }
      return this.isEdit ? 'Edit Payment Method' : 'Payment Method'
    }
  },

  methods: {
    submit () {
      if (this.validate()) {
        let self = this
        api.postRequest('backoffice/payment-method/', this.paymentMethod).then(
          (data) => {
            self.closeModal()
          }
        ).catch(function (error) {
          let errorData = error.response.data['Improperly formatted request']
          self.messageString = ''
          for (var value in errorData) {
            self.messageString += errorData[value] + '\n'
          }
          self.messagePopup()
        })
      }
    },

    clearPaymentSection () {
      this.setBlankCcDetail()
      this.bank = {
        valid: false,
        name: ''
      }
      this.paymentMethod.account = ''
      this.paymentMethod.routing = (this.paymentMethod.payment_type === 'CC') ? '000000001' : ''
    },

    setBlankCcDetail () {
      this.paymentMethod.cc_detail = {
        id: 0,
        code: '',
        bill_name: '',
        bill_address: '',
        bill_city: '',
        bill_state: '',
        bill_zip: ''
      }
      this.ccExpirationDate = {
        month: 1,
        year: this.currentYearLastTwoDigits
      }
    },

    setBlankLifetimeDiscount () {
      this.paymentMethod.lifetime_discount = {
        id: 0,
        amountType: 'A',
        amount: '0',
        employeeId: this.$store.state.auth.user.id
      }
    },

    setBlankDiscount () {
      this.paymentMethod.discounts = [
        {
          id: 0,
          amountType: 'A',
          amount: '0',
          effectiveDate: '',
          expirationDate: '',
          employeeId: this.$store.state.auth.user.id
        }
      ]
    },

    updateDiscounts (discounts) {
      this.paymentMethod.discounts = discounts
    },

    closeModal () {
      this.$store.dispatch('events/fireEvent', { name: 'payment-method-close', data: null })
    },

    checkBank () {
      let self = this
      api.makeRequest('backoffice/validate-bank/' + this.paymentMethod.routing).then(
        (data) => {
          self.bank = data.data.results
        }
      )
    },

    isWholeNumber (event) {
      event = (event) || window.event
      var charCode = (event.which) ? event.which : event.keyCode
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        event.preventDefault()
      } else {
        return true
      }
    },

    isNumber (event, value) {
      event = (event) || window.event
      var charCode = (event.which) ? event.which : event.keyCode
      if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 46) {
        event.preventDefault()
      } else if (charCode === 46 && value.includes('.')) {
        event.preventDefault()
      } else {
        return true
      }
    },

    isNumberWithHyphen (event, value) {
      event = (event) || window.event
      var charCode = (event.which) ? event.which : event.keyCode
      if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 45) {
        event.preventDefault()
      } else if (charCode === 45 && value.includes('-')) {
        event.preventDefault()
      } else {
        return true
      }
    },

    isLetter (event) {
      event = (event) || window.event
      var charCode = (event.which) ? event.which : event.keyCode

      if (!((charCode > 64 && charCode < 91) || (charCode > 96 && charCode < 123) || charCode < 32)) {
        event.preventDefault()
      } else {
        return true
      }
    },
    isCityField (event) {
      event = (event) || window.event
      var charCode = (event.which) ? event.which : event.keyCode

      if (!((charCode > 64 && charCode < 91) || (charCode > 96 && charCode < 123) || (charCode > 44 && charCode < 47) || charCode < 33)) {
        event.preventDefault()
      } else {
        return true
      }
    },

    formatNumber (object, attribute) {
      if (object[attribute].length === 0) {
        object[attribute] = '0'
      } else {
        object[attribute] = +object[attribute]
      }
    },

    range (start, end) {
      var ans = [start]
      for (let i = start + 1; i <= end; i++) {
        ans.push(i)
      }
      return ans
    },

    messagePopup: function () {
      this.$modal.show('MessagePopup')
    },

    validate () {
      let self = this
      let valid = true
      this.messageString = ''
      this.paymentMethod.discounts.forEach(function (discount) {
        let effDate = moment(discount.effectiveDate, 'YYYY-MM-DD')
        let expDate = moment(discount.expirationDate, 'YYYY-MM-DD')
        let today = moment(new Date().toISOString().slice(0, 10), 'YYYY-MM-DD')

        if (today.diff(effDate) > 0 && discount.id === 0) {
          valid = false
          self.messageString += 'New Discounts must have effective date starting today or later.' + '\n'
        }
        if (expDate.diff(effDate) < 0) {
          valid = false
          self.messageString += 'Expiration date cannot be before effective date.' + '\n'
        }
      })

      if (parseFloat(this.officeAmountAfterDiscount) < 0) {
        valid = false
        this.messageString += 'Total discounts cannot be greater than the base price.' + '\n'
      }

      if(this.ioPackageName === "MGA" && this.paymentMethod.office_amount == 0){
          valid = false
          this.messageString += 'Base price should be greater than zero.' + '\n'
      }

      if (valid === false) {
        this.messagePopup()
      }
      return valid
    }
  },

  watch: {
    'paymentMethod.routing': function () {
      if (this.paymentMethod.payment_type === 'ACH' && this.paymentMethod.routing.length > 0 && (this.paymentMethod.routing.length === 9 || this.bank.valid)) {
        this.checkBank()
      }
    },

    'ccExpirationDate': {
      deep: true,
      handler () {
        this.paymentMethod.cc_detail.expdate = ('0' + this.ccExpirationDate.month).slice(-2) + '/' + ('' + this.ccExpirationDate.year).slice(-2)
      }
    },

    'paymentMethod.cc_detail.bill_state': function (state) {
      this.paymentMethod.cc_detail.bill_state = state.toUpperCase()
    }
  }
}
</script>

<style scoped>
  .am-payment-method-title-bar {
    height: 40px;
  }

  .am-payment-method-title {
    font-size: 16px;
    float: left;
    margin: 10px 0 0 15px;
  }

  .am-payment-method-form {
    padding: 15px 15px 8px 15px;
    height: auto;
    overflow: hidden;
  }

  .am-payment-method-popup {
    position: relative;
    right: 150px;
    width: 720px;
    background-color: white;
    border: 1px lightgrey solid;
    height: auto;
    overflow: hidden;
  }

  .am-payment-method-form label {
    font-weight: bold;
  }

  .am-blue-border {
    display: block;
    width: 100%;
    border-top: 1px solid #4297d7;
    margin-bottom: 10px;
  }

  .col-md-1,.col-md-2,.col-md-3,.col-md-4,.col-md-5,.col-md-6,.col-md-9,.col-md-12 {
    padding-left: 5px;
    padding-right: 5px;
  }

  .form-row {
    margin-right: -5px;
    margin-left: -5px;
    display: flex;
    flex-wrap: wrap;
  }

  label {
    display: inline-block;
    margin-bottom: 0.5rem;
  }

  .w-100 {
    width: 100%;
  }

  .float-right {
    float: right;
  }

  .form-control {
    border-radius: 0;
  }

  .form-control.is-invalid {
    border-color: #c12e2a;
    padding-right: calc(1.5em + 0.75rem);
    background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
  }

  .form-control.is-invalid:focus {
    box-shadow: 0 0 0 0.2rem rgba(193, 46, 42, 0.25);
  }
</style>

<style>
  .am-vue-modal {
    background-color: white;
    text-align: left;
    border-radius: 0;
    box-shadow: 0 20px 60px -2px rgba(27, 33, 58, 0.4);
    padding: 0;
  }
</style>
